import { Component, OnInit } from '@angular/core';
import { NavbarService } from '../../core/navbar.service';
import { SeoService } from '../../core/seo.service';
import { ContentfulService } from '../../core/contentful.service';

export interface ShopItem {
  id: string;
  name: string;
  category: 'books' | 'apparel' | 'crafts' | 'prints';
  categoryLabel: string;
  price: number;
  description: string;
  imageUrl: string;
  inStock: boolean;
  featured?: boolean;
}

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrls: ['./shop.component.css']
})
export class ShopComponent implements OnInit {
  selectedCategory: string = 'all';
  selectedItem: ShopItem | null = null;
  orderSubmitted: boolean = false;
  buyerName: string = '';
  buyerEmail: string = '';
  buyerQuantity: number = 1;

  items: ShopItem[] = [
    {
      id: 'tshirt-bayou-heritage',
      name: 'Official Bayou Lacombe Museum Adult Shirt',
      category: 'apparel',
      categoryLabel: 'Apparel',
      price: 15,
      description: 'Official museum shirt featuring Bayou Lacombe Museum branding. 100% durable cotton, comfortable fit.',
      imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/5dGJp1QlWArea7lSL9GX63/5cf5763e99f2f06eb3a34b4bf9e20b92/Bayou_Lacombe_Museum_Tee.jpg',
      inStock: true,
      featured: true
    },
    {
      id: 'tshirt-youth',
      name: 'Youth Sizes | Bayou Lacombe Museum Shirt',
      category: 'apparel',
      categoryLabel: 'Apparel',
      price: 15,
      description: 'Youth sizing of the beloved Bayou Lacombe Museum shirt. Perfect for field trips and young junior historians.',
      imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/5J4PGjceZrgaYvCoJ5XHxq/f47ebc209393009f00f1bc3a42e06917/bayou_lacombe_museum-youth-tee.jpg',
      inStock: true,
      featured: true
    },
    {
      id: 'print-toussaint-candles',
      name: 'La Toussaint Twilight Archival Art Print (11x14)',
      category: 'prints',
      categoryLabel: 'Fine Art Prints',
      price: 30,
      description: 'High-resolution giclée print on archival cotton rag capturing the candlelit graves at Saint Henry Cemetery during Lacombe’s famous All Saints’ Day blessing.',
      imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/25J49fl7nXv1vbAQQ489m6/18bfc00b509745060c5024415a379b5f/AllSaints_2019.webp',
      inStock: true,
      featured: true
    },
    {
      id: 'mug-schoolhouse',
      name: 'Official 1912 Schoolhouse Commemorative Souvenir',
      category: 'crafts',
      categoryLabel: 'Souvenirs',
      price: 15,
      description: 'Souvenir featuring the historic 1912 wooden schoolhouse facade on South St. Mary Street.',
      imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/2xaL3IYyS5iU8bcXoWAOdX/9d1784950839614bbbcaa593c23ee090/whyblm.jpg',
      inStock: true
    },
    {
      id: 'book-lacombe-history',
      name: 'Bayou Lacombe & St. Tammany Parish Heritage History',
      category: 'books',
      categoryLabel: 'Historical Books',
      price: 22,
      description: 'The definitive photographic and archival history of Bayou Lacombe: Choctaw lifeways, Father Rouquette, timber, and brickmaking.',
      imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/3JofSza3JwQAv6K77cJ0Zj/5e57246f42e538b2ce9f36d65ef86602/LacombeMap1993.jpg',
      inStock: true
    }
  ];

  get filteredItems(): ShopItem[] {
    if (this.selectedCategory === 'all') {
      return this.items;
    }
    return this.items.filter(item => item.category === this.selectedCategory);
  }

  get orderMailtoLink(): string {
    if (!this.selectedItem) return '';
    const total = this.selectedItem.price * this.buyerQuantity;
    const subject = encodeURIComponent(`Museum Store Order Inquiry: ${this.selectedItem.name}`);
    const body = encodeURIComponent(
      `Hello Bayou Lacombe Museum Gift Shop,\n\nI would like to purchase the following item(s) from the online store:\n\nItem: ${this.selectedItem.name}\nQuantity: ${this.buyerQuantity}\nItem Price: $${this.selectedItem.price} each\nEstimated Total: $${total}\n\nBuyer Name: ${this.buyerName || 'Museum Supporter'}\nBuyer Email: ${this.buyerEmail || ''}\n\nPlease let me know how to arrange payment and pickup / shipping!\n\nThank you,\n${this.buyerName || 'Museum Supporter'}`
    );
    return `mailto:bayoulacombemuseum@gmail.com,info@bayoulacombemuseum.com?subject=${subject}&body=${body}`;
  }

  constructor(
    public nav: NavbarService,
    private seo: SeoService,
    private contentful: ContentfulService
  ) {}

  ngOnInit(): void {
    this.nav.show();
    this.seo.updateTags({
      title: 'Museum Heritage Gift Shop & Books',
      description: 'Support the Bayou Lacombe Museum through our heritage gift store: historic books, ceramic mugs, artisan prints, and Northshore Louisiana gifts.',
      url: '/shop'
    });

    // Attempt to dynamically fetch published products from Contentful
    this.contentful.getProducts().then(entries => {
      if (entries && entries.length > 0) {
        const dynamicItems: ShopItem[] = entries.map((e, idx) => {
          const f = e.fields;
          const imgUrl = f.image?.fields?.file?.url ? `https:${f.image.fields.file.url}` : this.items[idx % this.items.length].imageUrl;
          return {
            id: f.url || `product-${idx}`,
            name: f.title || 'Museum Item',
            category: (f.productType?.toLowerCase().includes('shirt') ? 'apparel' :
                       f.productType?.toLowerCase().includes('book') ? 'books' :
                       f.productType?.toLowerCase().includes('print') ? 'prints' : 'crafts') as any,
            categoryLabel: f.productType || 'Museum Item',
            price: typeof f.price === 'number' ? f.price : 15,
            description: f.description || 'Official Bayou Lacombe Museum collection item.',
            imageUrl: imgUrl,
            inStock: true,
            featured: idx === 0
          };
        });
        // Combine dynamic products with our curated heritage items (avoiding duplicates by name)
        const existingNames = new Set(dynamicItems.map(d => d.name.toLowerCase()));
        const uniqueCurated = this.items.filter(item => !existingNames.has(item.name.toLowerCase()));
        this.items = [...dynamicItems, ...uniqueCurated];
      }
    }).catch(() => {
      // Contentful products query fallback
    });
  }

  openOrderModal(item: ShopItem): void {
    this.selectedItem = item;
    this.orderSubmitted = false;
    this.buyerQuantity = 1;
  }

  closeOrderModal(): void {
    this.selectedItem = null;
    this.orderSubmitted = false;
  }

  submitOrderInquiry(): void {
    this.orderSubmitted = true;
  }
}
